// ABOUTME: Compact notice builder modal — centered SHModal, type + severity + title + body + url + audience.
// ABOUTME: Single Save action; publish / unpublish / resolve are grid actions.

import { Box, ButtonBase, InputBase, useTheme } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";

import { tokens } from "../../../../theme";
import SHModal from "../../../theme/Modal/SHModal";
import Shbutton from "../../../theme/buttons/Shbutton";
import ButtonGroup from "../../../theme/buttons/ButtonGroup";
import Subheading from "../../../theme/text/Subheading";
import Text from "../../../theme/text/Text";
import { InputDropdown } from "../../../theme/dropdowns/InputDropdown";
import ChatInput from "../../../theme/chat/ChatInput";
import MeldingTypeDropdown from "./MeldingTypeDropdown";
import {
    useAddNewsArticleMutation,
    useGetNewsArticleQuery,
    useUpdateNewsArticleMutation,
} from "../../../../newapi/global/newsRootSlice";
import { SEVERITY_OPTIONS } from "./noticeSeverity";
import { showToastMessage, showToastMessageRequestError } from "../../../../utils/toasts";

export { SEVERITY_OPTIONS, getSeverityPalette } from "./noticeSeverity";

// "customers" covers fulfilment companies — customers that fulfil orders for their
// own relations. "webshops" covers customers that only fulfil their own orders.
const AUDIENCE_OPTIONS = [
    { id: "customers", name: "customers" },
    { id: "webshops", name: "webshops" },
    { id: "relations", name: "relations" },
];

const buildPayload = ({ articleId, title, body, type, severity, url, audience, status }) => {
    const payload = {
        articleType: "melding",
        audience,
        status,
        title: title || null,
        body,
        type: type || null,
        severity: severity || null,
        url: url || null,
    };
    if (articleId) payload.id = articleId;
    return { payload };
};

const MCreateNotice = ({ open, handleClose, articleId }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const mode = theme.palette.mode;

    const [title, setTitle] = useState("");
    const [body, setBody] = useState(null);
    const [type, setType] = useState("");
    const [severity, setSeverity] = useState(null);
    const [url, setUrl] = useState("");
    const [audience, setAudience] = useState([]);
    const [saving, setSaving] = useState(false);

    const bodyRef = useRef(body);
    bodyRef.current = body;
    const handleBodyChange = useCallback((data) => {
        setBody(data?.content ?? null);
    }, []);

    const { data: existing } = useGetNewsArticleQuery(articleId, {
        skip: !open || !articleId,
    });

    const [addArticle] = useAddNewsArticleMutation();
    const [updateArticle] = useUpdateNewsArticleMutation();

    useEffect(() => {
        if (open && !articleId) {
            setTitle("");
            setBody(null);
            setType("");
            setSeverity(null);
            setUrl("");
            setAudience([]);
        }
    }, [open, articleId]);

    useEffect(() => {
        if (open && articleId && existing) {
            setTitle(existing.title ?? "");
            setBody(existing.body ?? null);
            setType(existing.type ?? "");
            setSeverity(existing.severity ?? null);
            setUrl(existing.url ?? "");
            setAudience(existing.audience ?? []);
        }
    }, [open, articleId, existing]);

    let isStandAlone = false;
    if (window.matchMedia("(display-mode: standalone)").matches) {
        isStandAlone = true;
    }

    const handleSubmit = async () => {
        if (saving) return;
        if (!title) return;
        setSaving(true);
        try {
            const args = buildPayload({ articleId, title, body, type, severity, url, audience, status: "draft" });
            if (articleId) {
                await updateArticle({ id: articleId, ...args }).unwrap();
            } else {
                await addArticle(args).unwrap();
            }
            showToastMessage("success", t("melding_saved") || "Melding opgeslagen");
            handleClose?.();
        } catch (err) {
            showToastMessageRequestError(t("save_failed") || "Opslaan mislukt", t, err);
        } finally {
            setSaving(false);
        }
    };

    const labelBoxSx = { width: 144, flexShrink: 0 };
    const inputWrapperSx = (multiline = false) => ({
        flex: 1,
        background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
        borderRadius: 4,
        color: colors.txt['primary'],
        fontSize: 14,
        display: "flex",
        alignItems: multiline ? "flex-start" : "center",
        minHeight: 32,
        px: 2,
        py: multiline ? 1 : 0,
    });

    return (
        <SHModal open={open} onClose={handleClose}>
            <Box
                sx={{
                    textAlign: "center",
                    paddingY: 4,
                    paddingX: 5,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                }}
            >
                <Subheading>
                    {articleId ? (t("edit_melding") || "Melding bewerken") : (t("create_melding") || "Melding aanmaken")}
                </Subheading>
            </Box>

            <Box
                sx={{
                    background: theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                    padding: 3,
                    pb: 1.5,
                    borderTopLeftRadius: 16,
                    borderTopRightRadius: 16,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.5,
                }}
            >
                {/* Type */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Text sx={labelBoxSx} light>{t("type")}</Text>
                    <MeldingTypeDropdown value={type} onChange={(v) => setType(v || "")} />
                </Box>

                {/* Severity (optional, theme-coloured) */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Text sx={labelBoxSx} light>{t("severity") || "Ernst"}</Text>
                    <Box sx={{ flex: 1, display: "flex", gap: 1 }}>
                        {SEVERITY_OPTIONS.map((opt) => {
                            const Icon = opt.Icon;
                            const selected = severity === opt.id;
                            const palette = colors[opt.getPaletteKey(mode)];
                            const fg = palette[400];
                            const bg = palette[100];
                            return (
                                <ButtonBase
                                    key={opt.id}
                                    onClick={() => setSeverity(selected ? null : opt.id)}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.75,
                                        px: 1.5,
                                        height: 32,
                                        borderRadius: 5,
                                        background: selected ? fg : bg,
                                        color: selected ? colors.txt["light"] : fg,
                                        fontFamily: "Roboto, sans-serif",
                                        fontSize: 13,
                                        fontWeight: 600,
                                        textTransform: "uppercase",
                                        transition: "150ms",
                                        "&:hover": { opacity: 0.85 },
                                    }}
                                >
                                    <Icon sx={{ fontSize: 16 }} />
                                    {t(opt.label) || opt.label}
                                </ButtonBase>
                            );
                        })}
                    </Box>
                </Box>

                {/* Title */}
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <Text sx={labelBoxSx} light>{t("title") || "Titel"}</Text>
                    <Box sx={inputWrapperSx()}>
                        <InputBase
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            sx={{
                                flex: 1,
                                lineHeight: "32px",
                                fontSize: 14,
                                fontFamily: "Roboto,sans-serif",
                                color: colors.txt['primary'],
                            }}
                        />
                    </Box>
                </Box>

                {/* Body — tiptap rich text */}
                <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                    <Text sx={{ ...labelBoxSx, pt: 1 }} light>{t("body") || "Tekst"}</Text>
                    <Box
                        sx={{
                            flex: 1,
                            background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                            borderRadius: 4,
                            pt: 2,
                            px: 1.5,
                            pb: 1,
                            // minHeight: 120,
                        }}
                    >
                        <ChatInput
                            key={articleId ? `edit-${articleId}-${existing ? "loaded" : "loading"}` : "new"}
                            hideSend
                            hideAttachments
                            initialContent={body}
                            onDataChange={handleBodyChange}
                            colors={colors}
                            theme={theme}
                            placeholder={t("write_something") || "Schrijf iets..."}
                        />
                    </Box>
                </Box>

                {/* URL */}
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <Text sx={labelBoxSx} light>{t("url") || "Link"}</Text>
                    <Box sx={inputWrapperSx()}>
                        <InputBase
                            value={url}
                            onChange={(e) => setUrl(e.target.value)}
                            sx={{
                                flex: 1,
                                lineHeight: "32px",
                                fontSize: 14,
                                fontFamily: "Roboto,sans-serif",
                                color: colors.txt['primary'],
                            }}
                            placeholder="https://"
                        />
                    </Box>
                </Box>

                {/* Audience */}
                <Box sx={{ display: "flex", alignItems: 'center', gap: 1 }}>
                    <Text sx={labelBoxSx} light>{t("visible_for") || "Zichtbaar voor"}</Text>
                    <InputDropdown
                        displayName="name"
                        valueKey="id"
                        noSelection={t("choose")}
                        options={AUDIENCE_OPTIONS}
                        selected={audience}
                        multiple
                        useTranslation
                        onChange={(selected) => setAudience(selected)}
                        closeOnSelection={false}
                    />
                </Box>
            </Box>

            <Box
                component="footer"
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: 1,
                    paddingBottom: isStandAlone ? "32px" : "16px",
                    paddingRight: 3,
                    paddingTop: 2,
                    zIndex: 1058,
                    position: "relative",
                    background: theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                    borderBottomLeftRadius: 24,
                    borderBottomRightRadius: 24,
                    "@media screen and (max-width: 47.9375em)": { paddingRight: 0 },
                }}
            >
                <Shbutton
                    isCancel={true}
                    className={"always-visible"}
                    onClick={handleClose}
                    color="primary"
                    variant="text"
                    startIcon={<ArrowRightAltIcon />}
                >
                    {t("cancel")}
                </Shbutton>
                <ButtonGroup version={2}>
                    <Shbutton
                        isLoading={saving}
                        className="group-btn"
                        color={theme.palette.mode === "dark" ? "blue" : "primary"}
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={!title}
                    >
                        {articleId ? t("save") : (t("create") || "Toevoegen")}
                    </Shbutton>
                </ButtonGroup>
            </Box>
        </SHModal>
    );
};

export default MCreateNotice;
